import { Drawer, Button, Empty, Typography, InputNumber, Spin } from 'antd';
import { DeleteOutlined, ShoppingCartOutlined, CloseOutlined } from '@ant-design/icons';
import { useCartDrawer } from '@contexts';
import { useCart, useAuth, useOrders, calculatePriceWithTier, getTierDiscountPercent } from '@hooks';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';

const { Title, Paragraph, Text } = Typography;

export const CartDrawer = () => {
  const { drawerOpen, closeDrawer } = useCartDrawer();
  const { profile: { data: profile } } = useAuth();
  const isAuthenticated = !!profile;
  const { cart, isLoading, updateQuantity, removeFromCart, clearCart } = useCart(isAuthenticated);
  const { createCheckoutSession } = useOrders();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  
  // Get user tier for discount calculation
  const userTier = profile?.tier || 'retail';
  const tierDiscountPercent = getTierDiscountPercent(userTier);
  
  // Calculate total with tier discount
  const totalWithDiscount = useMemo(() => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => {
      const priceWithDiscount = tierDiscountPercent > 0 
        ? calculatePriceWithTier(item.productPrice, userTier)
        : item.productPrice;
      return total + (priceWithDiscount * item.quantity);
    }, 0);
  }, [cart, tierDiscountPercent, userTier]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getProductImage = (imageUrl: string, productName: string) => {
    if (imageUrl && imageUrl.startsWith('http')) {
      return imageUrl;
    } else if (imageUrl) {
      const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production';
      const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${imageUrl}`;
    }
    return `https://placehold.co/100x100/181717/F74902?text=${encodeURIComponent(productName)}`;
  };

  const handleQuantityChange = async (cartItemId: string, newQuantity: number) => {
    try {
      await updateQuantity(cartItemId, newQuantity);
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleRemoveItem = async (cartItemId: string) => {
    try {
      await removeFromCart(cartItemId);
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      closeDrawer();
      navigate('/login');
      return;
    }

    if (!cart || cart.items.length === 0) {
      return;
    }

    setCheckingOut(true);
    try {
      await createCheckoutSession.mutateAsync({
        lineItems: cart.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          variantId: item.variantId,
        })),
        successUrl: `${window.location.origin}/order-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/merch`,
      });

      // Clear cart after successful checkout session creation
      await clearCart();
      closeDrawer();
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckingOut(false);
    }
  };

  return (
    <Drawer
      title={
        <div className="flex items-center gap-3 mt-2">
          <ShoppingCartOutlined className="text-orangicide text-2xl" />
          <Title level={3} className="font-orator !text-white !mb-0 !leading-none uppercase">
            Shopping Cart
          </Title>
        </div>
      }
      placement="right"
      open={drawerOpen}
      onClose={closeDrawer}
      width={450}
      closeIcon={<CloseOutlined className="text-white text-xl mt-2" />}
      className="cart-drawer"
      styles={{
        header: {
          backgroundColor: '#181717',
          borderBottom: '2px solid #F74902',
          padding: '20px 24px',
        },
        body: {
          backgroundColor: '#000000',
          padding: 0,
        },
      }}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Spin size="large" />
        </div>
      ) : !cart || cart.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full px-6">
          <Empty
            image={<ShoppingCartOutlined style={{ fontSize: 80, color: '#F74902' }} />}
            description={
              <Paragraph className="font-orator text-zinc-400 text-lg mt-4">
                Your cart is empty
              </Paragraph>
            }
          />
          <Button
            type="primary"
            size="large"
            onClick={() => {
              closeDrawer();
              navigate('/merch');
            }}
            className="btn-orangicide font-orator text-lg mt-6"
          >
            Continue Shopping
          </Button>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {/* Cart Items List */}
          <div className="flex-1 overflow-y-auto p-6">
            {cart.items.map((item) => (
              <div
                key={item.id}
                className="bg-zinc-900 border border-zinc-700 p-4 mb-4 hover:border-orangicide/50 transition-colors"
              >
                <div className="flex gap-4">
                  {/* Product Image */}
                  <div className="w-20 h-20 flex-shrink-0 bg-zinc-800 overflow-hidden border border-zinc-700">
                    <img
                      src={getProductImage(item.productImage, item.productName)}
                      alt={item.productName}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <Paragraph className="font-orator text-white text-base font-bold !mb-1 truncate">
                      {item.productName}
                    </Paragraph>
                    
                    {/* ITEM information */}
                    {item.sku && (
                      <Text className="font-orator !text-xs block mb-1">
                        <span className="text-orangicide font-semibold">Item:</span> <span className="text-white font-semibold">{item.sku}</span>
                      </Text>
                    )}
                    
                    {/* Size information */}
                    {item.size && (
                      <Text className="font-orator font-semibold text-orangicide !text-xs block mb-4">
                        <span className="text-orangicide">Size:</span> <span className="text-white">{item.size}{item.chainSize ? ` / ${item.chainSize}` : ''}</span>
                      </Text>
                    )}
                    
                    {/* Price with tier discount */}
                    <div className="mb-1 flex items-center flex-wrap">
                      {tierDiscountPercent > 0 ? (
                        <>
                          <Text className="font-orator text-white text-sm line-through mr-2">
                            {formatPrice(item.productPrice)}
                          </Text>
                          <Text className="font-orator font-bold text-orangicide text-lg mr-2">
                            {formatPrice(calculatePriceWithTier(item.productPrice, userTier))}
                          </Text>
                          <Text className="font-orator font-semibold text-white !text-sm ml-0 whitespace-nowrap">
                            -{tierDiscountPercent}%
                          </Text>
                        </>
                      ) : (
                        <Text className="font-orator text-orangicide text-lg font-bold">
                          {formatPrice(item.productPrice)}
                        </Text>
                      )}
                    </div>

                    {/* Quantity Control */}
                    <div className="flex items-center gap-3 mt-4">
                      <InputNumber
                        min={1}
                        max={99}
                        value={item.quantity}
                        onChange={(value) => handleQuantityChange(item.id, value || 1)}
                        className="w-20 font-orator [&_input]:!font-orator"
                        size="small"
                      />
                      <Text className="font-orator font-semibold !text-white text-sm">
                        Subtotal:{' '}
                        <span className="text-orangicide">
                          {formatPrice(
                            tierDiscountPercent > 0 
                              ? calculatePriceWithTier(item.productPrice, userTier) * item.quantity
                              : item.subtotal
                          )}
                        </span>
                      </Text>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveItem(item.id)}
                    className="!text-white [&:hover_svg]:!drop-shadow-[0_0_2px_#F74902] !bg-transparent hover:!bg-transparent"
                  />
                </div>
              </div>
            ))}

            {/* Clear Cart Button */}
            {cart.items.length > 0 && (
              <Button
                type="default"
                onClick={handleClearCart}
                className="font-orator w-full mt-2 !bg-transparent !text-[#F74902] !border !border-[#F74902] hover:!bg-transparent hover:!text-[#F74902] hover:!border-[#F74902] hover:!shadow-[0_4px_8px_rgba(247,73,2,0.3)] hover:!-translate-y-0.5 transition-all duration-200"
              >
                Clear Cart
              </Button>
            )}
          </div>

          {/* Cart Summary Footer */}
          <div className="border-t-2 border-orangicide bg-zinc-900 p-6">
            {/* Total Items */}
            <div className="flex justify-between items-center mb-3">
              <Text className="font-orator !text-white text-base font-semibold">
                Total Items:
              </Text>
              <Text className="font-orator !text-white text-base font-bold">
                {cart.totalItems}
              </Text>
            </div>

            {/* Total Amount */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-zinc-700">
              <Text className="font-orator text-orangicide text-xl font-bold uppercase">
                Total:
              </Text>
              <div className="text-right flex items-center gap-2 justify-end">
                {tierDiscountPercent > 0 && (
                  <Text className="font-orator font-semibold text-white text-sm line-through">
                    {formatPrice(cart.totalAmount)}
                  </Text>
                )}
                <Text className="font-orator text-orangicide text-2xl font-bold">
                  {formatPrice(totalWithDiscount)}
                </Text>
              </div>
            </div>

            {/* Checkout Button */}
            <Button
              type="primary"
              size="large"
              loading={checkingOut}
              onClick={handleCheckout}
              className="btn-orangicide font-orator text-xl w-full h-14 uppercase shadow-lg shadow-orangicide/30 hover:shadow-orangicide/50"
            >
              {isAuthenticated ? 'Proceed to Checkout' : 'Login to Checkout'}
            </Button>

            <Button
              type="primary"
              size="large"
              onClick={() => {
                closeDrawer();
                navigate('/merch');
              }}
              className="btn-orangicide font-orator text-base w-full mt-3 uppercase"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
};

